import React from 'react';
import { Download, FileSpreadsheet, RefreshCw, Trash2 } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import { isActiveJob, useExportJobs } from './ExportJobsContext';

const STATUS_LABELS = {
  queued: 'В очереди',
  running: 'Выполняется',
  completed: 'Готово',
  failed: 'Ошибка',
  cancelled: 'Отменено',
};

const STATUS_CLASSES = {
  queued: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  running: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  completed: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  cancelled: 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
};

export const ExportJobsLauncher = () => {
  const { activeCount, setIsPanelOpen } = useExportJobs();

  return (
    <Button
      type="button"
      variant="outline"
      size="icon"
      className="fixed bottom-6 right-6 z-40 h-11 w-11 rounded-full bg-background shadow-lg"
      onClick={() => setIsPanelOpen(true)}
      title="Выгрузки"
    >
      <FileSpreadsheet className="h-5 w-5" />
      {activeCount > 0 && (
        <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[11px] font-semibold text-primary-foreground">
          {activeCount}
        </span>
      )}
    </Button>
  );
};

const ExportJobsPanel = () => {
  const {
    jobs,
    isPanelOpen,
    setIsPanelOpen,
    refreshJobs,
    downloadJob,
    deleteJob,
  } = useExportJobs();

  return (
    <Sheet open={isPanelOpen} onOpenChange={setIsPanelOpen}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>Выгрузки</SheetTitle>
          <SheetDescription>Фоновые Excel-отчеты по звонкам</SheetDescription>
        </SheetHeader>

        <div className="mt-5 flex justify-end">
          <Button type="button" variant="outline" size="sm" onClick={refreshJobs}>
            <RefreshCw className="h-4 w-4" />
            Обновить
          </Button>
        </div>

        <div className="mt-4 space-y-3">
          {jobs.length === 0 && (
            <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
              Нет выгрузок.
            </div>
          )}

          {jobs.map((job) => {
            const normalizedStatus = String(job.status ?? '').toLowerCase();
            const progress = Number(job.progress ?? 0);
            const canDownload = normalizedStatus === 'completed' && job.filename;

            return (
              <div key={job.id} className="rounded-md border bg-background p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">
                      {job.filename ?? `Отчет ${formatDate(job.createdAt)}`}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {formatDate(job.createdAt)}
                    </div>
                  </div>
                  <Badge className={STATUS_CLASSES[normalizedStatus] ?? STATUS_CLASSES.queued}>
                    {STATUS_LABELS[normalizedStatus] ?? job.status}
                  </Badge>
                </div>

                <div className="mt-4 space-y-2">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>
                      {job.processedRows ?? 0} / {job.totalRows ?? 0}
                    </span>
                    <span>{Math.max(0, Math.min(100, progress))}%</span>
                  </div>
                  <Progress value={Math.max(0, Math.min(100, progress))} />
                </div>

                {job.error && (
                  <div className="mt-3 rounded-md bg-red-50 p-2 text-xs text-red-800 dark:bg-red-950 dark:text-red-300">
                    {job.error}
                  </div>
                )}

                <div className="mt-4 flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!canDownload}
                    onClick={() => downloadJob(job)}
                    title="Скачать"
                  >
                    <Download className="h-4 w-4" />
                    Скачать
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={isActiveJob(job)}
                    onClick={() => deleteJob(job.id)}
                    title="Удалить"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
};

const formatDate = (value) => {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

export default ExportJobsPanel;
